import api from "../config.json";
import axios from "../httpServices/httpService";

/**
 * Cache primitives for the slow-changing master data (equipment, equipmentgroup,
 * downtimereason, qualityreason, operator, tool, component).
 *
 * Why this exists
 * ---------------
 * NavBarUser.componentDidMount used to removeItem() all seven keys as its first
 * statement, then immediately read them back through storeDataInLocalStorage().
 * The cache therefore never survived a page reload - every F5 was a guaranteed
 * miss on all seven keys, and each one was awaited in series.
 *
 * Storage layout
 * --------------
 * The value stays exactly where it was, in exactly the format it was in: a raw
 * JSON array under the plain key name. Roughly fifteen components still do
 * JSON.parse(localStorage.getItem('equipment')) directly, so changing the shape
 * would break them. Metadata lives in separate sidecar keys.
 *
 *   equipment          -> "[ ...the array... ]"     (unchanged, public)
 *   mdc:ts:equipment   -> "1723980000000"           (when it was fetched)
 *   mdc:stamp:equipment-> "2.4.a1b2c3"              (who/what it was fetched for)
 *
 * Invalidation
 * ------------
 * 1. Missing value is ALWAYS a miss, whatever the sidecars say. Existing call
 *    sites that invalidate by removeItem() keep working untouched:
 *    authService.logout(), NavBarUser's company switch, Dashboard2 and
 *    NagataConcept2's company carousel.
 * 2. Stamp mismatch is a miss. The stamp folds in config UIVersion and a hash of
 *    the auth token, so a UI version bump, a re-login, or a company switch all
 *    drop the cache without anyone having to remember to clear it.
 * 3. Past the TTL the entry is served but marked stale, so the caller can render
 *    instantly and refresh in the background.
 */

const TS_PREFIX = "mdc:ts:";
const STAMP_PREFIX = "mdc:stamp:";

const DEFAULT_TTL_MINUTES = 10;

/**
 * Lists the server can trim with ?view=lite (drops the audit trail and the job
 * mapping that only the settings pages read; those pages fetch the full list
 * directly). Keeps the cached copies small enough for the localStorage quota.
 */
const LITE_VIEW_KEYS = new Set(["equipment", "component", "tool"]);

const masterUrl = (apiName) =>
  api.dataEntryUrl + apiName + (LITE_VIEW_KEYS.has(apiName) ? "?view=lite" : "");

/** The seven keys NavBarUser prefetches. */
export const MASTER_DATA_KEYS = [
  "equipment",
  "equipmentgroup",
  "downtimereason",
  "qualityreason",
  "operator",
  "tool",
  "component",
];

const ttlMs = () => {
  const configured = Number(api && api.MasterCacheTTLMinutes);
  const minutes =
    isFinite(configured) && configured > 0 ? configured : DEFAULT_TTL_MINUTES;
  return minutes * 60 * 1000;
};

/** djb2. Only used to keep the full token out of a second storage slot. */
const hash = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
};

/**
 * Identity a cache entry belongs to. Reading the token straight out of storage
 * rather than going through authService.getCurrentUser() keeps this cheap and
 * side-effect free (getCurrentUser reassigns axios default headers on every call).
 */
const currentStamp = () => {
  let token = "";
  try {
    token = localStorage.getItem("token") || "";
  } catch (e) {
    token = "";
  }
  return `${(api && api.UIVersion) || "0"}.${hash(token)}`;
};

/**
 * @returns {{value: any, isFresh: boolean}|null} null means "go to the network".
 */
export function readCache(key) {
  let raw = null;
  try {
    raw = localStorage.getItem(key);
  } catch (e) {
    return null;
  }

  // Absent, or explicitly cleared by one of the removeItem() call sites.
  if (!raw) return null;

  let value;
  try {
    value = JSON.parse(raw);
  } catch (e) {
    // Truncated or corrupt entry; drop it rather than handing back garbage.
    clearCache(key);
    return null;
  }

  let ts = 0;
  let stamp = "";
  try {
    ts = Number(localStorage.getItem(TS_PREFIX + key)) || 0;
    stamp = localStorage.getItem(STAMP_PREFIX + key) || "";
  } catch (e) {
    return null;
  }

  // Different company/user/UI version, or an entry written by the pre-TTL code
  // that has no sidecars at all. Either way, refetch once.
  if (stamp !== currentStamp()) return null;

  return { value, isFresh: ts > 0 && Date.now() - ts < ttlMs() };
}

export function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    localStorage.setItem(TS_PREFIX + key, String(Date.now()));
    localStorage.setItem(STAMP_PREFIX + key, currentStamp());
  } catch (e) {
    // Quota exceeded, or storage blocked in a private window. Drop the entry so
    // we never serve a half-written cache; the next read falls back to network.
    clearCache(key);
  }
}

export function clearCache(key) {
  try {
    localStorage.removeItem(key);
    localStorage.removeItem(TS_PREFIX + key);
    localStorage.removeItem(STAMP_PREFIX + key);
  } catch (e) {
    /* storage unavailable - nothing to clear */
  }
}

/** Clears every master-data key plus its sidecars. */
export function clearAllMasterData() {
  MASTER_DATA_KEYS.forEach(clearCache);
}

const inFlight = new Map();

/**
 * Collapses concurrent requests for the same key into one. On a cold cache
 * several report components can mount at once and each ask for 'component';
 * without this they each fire their own GET.
 */
export function dedupe(key, fetcher) {
  const existing = inFlight.get(key);
  if (existing) return existing;

  const pending = Promise.resolve().then(fetcher);
  const forget = () => {
    inFlight.delete(key);
  };
  // Deliberately not Promise.prototype.finally: the react-app-polyfill imports
  // are commented out in index.js, so we stick to ES6 promise methods.
  pending.then(forget, forget);
  inFlight.set(key, pending);

  return pending;
}

/**
 * Promise.allSettled equivalent. Hand-rolled for the same polyfill reason.
 * @returns {Promise<{ok: boolean, value?: any, error?: any}>}
 */
export function settle(promise) {
  return Promise.resolve(promise).then(
    (value) => ({ ok: true, value }),
    (error) => ({ ok: false, error })
  );
}

/**
 * Reads a master list through the cache: serves a cached copy straight away
 * (refreshing it in the background once past the TTL), otherwise fetches it once,
 * sharing the request with any concurrent callers.
 *
 * `onRefresh` is optional and only fires when a background refresh lands, which
 * lets a caller re-render with fresh data without blocking the first paint.
 *
 * Use this instead of axios.get(api.dataEntryUrl + "equipment") and friends,
 * especially inside polling loops.
 */
export function getMasterData(apiName, onRefresh) {
  const fetchAndStore = () =>
    dedupe(apiName, async () => {
      const response = await axios.get(masterUrl(apiName));

      if (!response || !response.data) {
        throw new Error("API response is empty");
      }

      writeCache(apiName, response.data);
      return response.data;
    });

  const cached = readCache(apiName);

  if (cached) {
    if (!cached.isFresh) {
      fetchAndStore()
        .then((fresh) => {
          if (typeof onRefresh === "function") onRefresh(fresh);
        })
        .catch(() => {
          // Offline, or the server is down. The cached copy we already
          // returned stands; nothing to report to the user.
        });
    }
    return Promise.resolve(cached.value);
  }

  return fetchAndStore();
}

const companyCache = new Map();

/**
 * Same idea for a master list fetched with an explicit Company header (the
 * company carousels in Dashboard2/Nagata). Kept in memory only, so it never
 * overwrites the signed-in company's localStorage copy that other screens read
 * raw. Keyed by the auth stamp too, so a re-login or UI version bump drops it.
 */
export function getCompanyMasterData(company, apiName) {
  const key = `${currentStamp()}|${company}|${apiName}`;
  const hit = companyCache.get(key);
  if (hit && Date.now() - hit.ts < ttlMs()) return Promise.resolve(hit.value);

  return dedupe(key, async () => {
    const response = await axios.get(masterUrl(apiName), {
      headers: { Company: company },
    });

    if (!response || !response.data) {
      throw new Error("API response is empty");
    }

    companyCache.set(key, { value: response.data, ts: Date.now() });
    return response.data;
  });
}

export default {
  MASTER_DATA_KEYS,
  readCache,
  writeCache,
  clearCache,
  clearAllMasterData,
  dedupe,
  settle,
  getMasterData,
  getCompanyMasterData,
};
